import { type UseQueryResult, useQueries, useQueryClient } from "@tanstack/react-query";
import { createCatalogClient, createPosClient } from "@tcg/api-client";

import { useAuth } from "@/auth/use-auth";
import type { Permission } from "@/auth/types";
import type { PolicyType } from "@/features/content/api/contentApi";
import { useContentApi } from "@/features/content/api/useContentApi";
import { usePaymentSettingsApi } from "@/features/payments/settingsApi";
import { useStaffApi } from "@/features/settings/staffApi";
import { useShippingApi } from "@/features/shipping/shippingApi";
import { useTaxAdminApi } from "@/features/tax/api";
import { unwrap } from "@/lib/api/errors";
import { useServiceClient } from "@/lib/api/use-api";
import { pollFor, useLiveTopics } from "@/realtime/hooks";

import { type ChecklistInputs, checklistSteps, type Source } from "./onboarding";

export const ONBOARDING_KEY = ["onboarding"] as const;

/** The areas whose settings make up the list (design/07 §9 topics) and who may listen. */
const LIVE_TOPICS: ReadonlyArray<readonly [string, Permission]> = [
  ["staff:cms", "cms.edit"],
  ["staff:tax", "tax.read"],
  ["staff:payments", "payments.read"],
  ["staff:orders", "orders.read"],
  ["staff:pos", "settings.read"],
  ["staff:catalog", "products.read"],
  ["staff:staff", "staff.read"],
];

function source<T, R>(query: UseQueryResult<R>, allowed: boolean, pick: (data: R) => T): Source<T> {
  if (!allowed) {
    return { state: "no_access" };
  }
  if (query.isPending) {
    return { state: "loading" };
  }
  if (query.isError) {
    return { state: "error" };
  }
  return { state: "ok", data: pick(query.data) };
}

/**
 * Every step's answer from its own service (each loads and fails alone). A saved setting anywhere
 * refreshes the list through the realtime topics of those areas; 60 s poll while the hub is away.
 */
export function useOnboardingStatus(enabled: boolean) {
  const { can, session } = useAuth();
  const content = useContentApi();
  const tax = useTaxAdminApi();
  const payments = usePaymentSettingsApi();
  const shipping = useShippingApi();
  const staff = useStaffApi();
  const pos = useServiceClient(createPosClient);
  const catalog = useServiceClient(createCatalogClient);
  const allowed = (permission: Permission) => enabled && can(permission);
  const queryClient = useQueryClient();
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ONBOARDING_KEY });
  const topics = LIVE_TOPICS.filter(([, permission]) => can(permission)).map(([topic]) => topic);
  const live = useLiveTopics(topics, refresh, {
    enabled: enabled && topics.length > 0,
    onResync: refresh,
  });
  const poll = pollFor(live, 60_000);

  const perms = {
    policies: allowed("cms.edit"),
    tax: allowed("tax.read"),
    methods: allowed("payments.read"),
    zones: allowed("orders.read"),
    registers: allowed("settings.read"),
    products: allowed("products.read"),
    staff: allowed("staff.read"),
  };
  const common = {
    refetchInterval: poll,
    retry: false,
    meta: { silent: true, errorToast: false },
  } as const;
  const [policies, taxSetting, methods, zones, registers, products, people] = useQueries({
    queries: [
      {
        ...common,
        queryKey: [...ONBOARDING_KEY, "policies"],
        enabled: perms.policies,
        queryFn: async () => {
          const [tokushoho, kobutsu, contact] = await Promise.all([
            content.getPolicy("tokushoho" satisfies PolicyType),
            content.getPolicy("kobutsu" satisfies PolicyType),
            content.getPolicy("contact" satisfies PolicyType),
          ]);
          return { tokushoho: tokushoho.fields, kobutsu: kobutsu.fields, contact: contact.fields };
        },
      },
      {
        ...common,
        queryKey: [...ONBOARDING_KEY, "tax"],
        enabled: perms.tax,
        queryFn: ({ signal }: { signal: AbortSignal }) => tax.settings(null, signal),
      },
      {
        ...common,
        queryKey: [...ONBOARDING_KEY, "payments"],
        enabled: perms.methods,
        queryFn: ({ signal }: { signal: AbortSignal }) => payments.methods(signal),
      },
      {
        ...common,
        queryKey: [...ONBOARDING_KEY, "zones"],
        enabled: perms.zones,
        queryFn: ({ signal }: { signal: AbortSignal }) => shipping.zones(signal),
      },
      {
        ...common,
        queryKey: [...ONBOARDING_KEY, "registers"],
        enabled: perms.registers,
        queryFn: async ({ signal }: { signal: AbortSignal }) =>
          (
            await unwrap(
              pos.GET("/v1/admin/pos/registers", { params: { query: { limit: 100 } }, signal }),
            )
          ).data,
      },
      {
        ...common,
        queryKey: [...ONBOARDING_KEY, "products"],
        enabled: perms.products,
        queryFn: async ({ signal }: { signal: AbortSignal }) => {
          const page = await unwrap(
            catalog.GET("/v1/admin/products", {
              params: { query: {} },
              querySerializer: () => "limit=1",
              signal,
            }),
          );
          return { count: page.data.length };
        },
      },
      {
        ...common,
        queryKey: [...ONBOARDING_KEY, "staff"],
        enabled: perms.staff,
        queryFn: async ({ signal }: { signal: AbortSignal }) =>
          (await staff.staff({ limit: 100 }, signal)).data,
      },
    ],
  });

  const inputs: ChecklistInputs = {
    policies: source(policies, perms.policies, (d) => d),
    tax: source(taxSetting, perms.tax, (d) => ({
      invoiceRegistrationNumber: d.invoiceRegistrationNumber,
      registrationNumberIsPlaceholder: d.registrationNumberIsPlaceholder,
      businessType: d.businessType,
    })),
    methods: source(methods, perms.methods, (d) => d.data),
    zones: source(zones, perms.zones, (d) => d),
    registers: source(registers, perms.registers, (d) => d),
    products: source(products, perms.products, (d) => d),
    staff: source(people, perms.staff, (d) => d),
    me: { id: session?.user.id ?? "", totpEnabled: session?.user.totpEnabled },
  };
  return checklistSteps(inputs);
}
